import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "./ReviewModal.module.css";
import { StarRating } from "./StarRating";

interface FotoPonto {
  ponto: string;
  arquivo?: File;
  id?: number; // id do arquivo existente no backend
  url?: string; // URL da foto existente
}

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  compraId: number;
  pacoteId: number;
  onSubmitted?: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  compraId,
  pacoteId,
  onSubmitted,
}) => {
  const [nota, setNota] = useState<number>(5);
  const [comentario, setComentario] = useState<string>("");
  const [fotos, setFotos] = useState<(File | { id: number; url: string })[]>([]);
  const [fotosPorPonto, setFotosPorPonto] = useState<FotoPonto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pontosPacote, setPontosPacote] = useState<string[]>([]);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const BASE_URL = process.env.REACT_APP_API_URL;

  // ==================== BUSCA DADOS AO ABRIR MODAL ====================
  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    setLoading(true);

    const fetchData = async () => {
      try {
        // 1️⃣ Avaliação existente
        const res = await axios.get(`${BASE_URL}/avaliacoes/usuario/${compraId}`, { headers });
        const avaliacao = res.data;

        if (avaliacao) {
          setNota(avaliacao.nota);
          setComentario(avaliacao.comentario);

          // Fotos gerais existentes
          const fotosExistentes = (avaliacao.fotos || []).map((id: number) => ({
            id,
            url: `${BASE_URL}/uploads/${id}`,
          }));
          setFotos(fotosExistentes);

          // Fotos por ponto existentes
          const fotosPontoExistentes = (avaliacao.fotosPorPonto || []).map((f: any) => {
            return {
              ponto: f.ponto,
              id: f.fotoId,
              url: f.url,
            };
          });
          setFotosPorPonto(fotosPontoExistentes);
        } else {
          setNota(5);
          setComentario("");
          setFotos([]);
          setFotosPorPonto([]);
        }

        // 2️⃣ Pontos do pacote
        const pontosRes = await axios.get(`${BASE_URL}/pacotes/${pacoteId}/pontos`, { headers });
        const pontos: string[] = pontosRes.data || [];
        setPontosPacote(pontos);

        // Se não existe nenhuma fotoPorPonto carregada, cria uma entrada inicial sem ponto selecionado
        setFotosPorPonto((prev) => {
          if (prev.length > 0) return prev;
          return [{ ponto: "" }];
        });

        // Se o pacote só tem 1 ponto → já pré-seleciona
        if (pontos.length === 1) {
          setFotosPorPonto((prev) =>
            prev.map((fp) => ({ ...fp, ponto: pontos[0] }))
          );
        }
      } catch (err) {
        console.error("Erro ao buscar avaliação ou pontos:", err);
        setError("Erro ao carregar avaliação");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, compraId, pacoteId]);

  // ==================== HANDLERS ====================
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const novos = Array.from(e.target.files);
      setFotos((prev) => [...prev, ...novos]);
    }
  };

  const removerFoto = (index: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadArquivo = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${BASE_URL}/uploads`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
    return res.data.id;
  };

  const handleSubmit = async () => {
    setError(null);

    if (nota < 1 || nota > 5) {
      setError("A nota deve ser entre 1 e 5");
      return;
    }

    // ✅ Validação só para fotos por ponto
    const fotosInvalidas = fotosPorPonto.filter(
      (fp) => (fp.arquivo || fp.id) && !fp.ponto
    );
    if (fotosInvalidas.length > 0) {
      setError("Selecione um ponto para cada foto enviada por ponto");
      return;
    }

    setLoading(true);

    try {
      // 1️⃣ Fotos gerais → upload apenas arquivos novos
      const fotosIds = await Promise.all(
        fotos.map((f) =>
          f instanceof File ? uploadArquivo(f) : f.id
        )
      );

      // 2️⃣ Fotos por ponto → upload novos arquivos
      const fotosPorPontoDTO = await Promise.all(
        fotosPorPonto
          .filter((fp) => fp.arquivo || fp.id)
          .map((fp) =>
            fp.arquivo
              ? uploadArquivo(fp.arquivo).then((id) => ({
                ponto: fp.ponto,
                fotoId: id,
              }))
              : { ponto: fp.ponto, fotoId: fp.id }
          )
      );

      // 3️⃣ Enviar avaliação
      await axios.post(
        `${BASE_URL}/avaliacoes/${compraId}`,
        { nota, comentario, fotos: fotosIds, fotosPorPonto: fotosPorPontoDTO },
        { headers }
      );

      if (onSubmitted) onSubmitted();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || "Erro ao enviar avaliação");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop}>
      <div className={styles.modal}>
        <h5 className={styles.title}>Review purchase #{compraId}</h5>

        {/* Rating */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Rating</label>
          <StarRating value={nota} onChange={setNota} />
        </div>

        {/* Comment */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Comment</label>
          <textarea
            className={styles.textarea}
            placeholder="Write your feedback here..."
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
        </div>

        {/* General photos */}
        <div className={styles.formGroup}>
          <label className={styles.label}>General photos (optional)</label>
          <label className={styles.uploadLabel}>
            <input
              type="file"
              className={styles.fileInputHidden}
              multiple
              onChange={handleFileChange}
            />
            + Upload Photos
          </label>
          <div className={styles.previewGrid}>
            {fotos.map((f, idx) => {
              const src = f instanceof File ? URL.createObjectURL(f) : f.url;
              return (
                <div key={idx} className={styles.previewItem}>
                  <img src={src} alt="Photo" className={styles.previewImg} />
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => removerFoto(idx)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Photos per stop */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Photos by stop (optional)</label>

          {/* Botão único de upload que adiciona várias entradas */}
          <label className={styles.uploadLabel}>
            <input
              type="file"
              multiple
              className={styles.fileInputHidden}
              onChange={(e) => {
                if (!e.target.files) return;
                const novos = Array.from(e.target.files).map((file) => ({
                  ponto: "", // ainda não selecionado
                  arquivo: file,
                }));
                setFotosPorPonto((prev) => [...prev, ...novos]);
              }}
            />
            + Upload Stop Photos
          </label>

          {/* Lista de fotos já adicionadas */}
          <div className={styles.previewGrid}>
            {fotosPorPonto.map((fp, index) => {
              // Corrigido: adiciona BASE_URL se for foto existente do backend
              const src = fp.arquivo
                ? URL.createObjectURL(fp.arquivo)
                : fp.url
                  ? `${BASE_URL}${fp.url}`
                  : undefined;

              return (
                <div key={index} className={styles.previewItem}>
                  {src && <img src={src} alt="Stop" className={styles.previewImg} />}

                  {/* Select aparece ao lado da foto */}
                  <select
                    className={styles.input}
                    value={fp.ponto}
                    onChange={(e) =>
                      setFotosPorPonto((prev) => {
                        const copy = [...prev];
                        copy[index].ponto = e.target.value;
                        return copy;
                      })
                    }
                  >
                    <option value="">Select a stop</option>
                    {pontosPacote.map((ponto) => (
                      <option key={ponto} value={ponto}>
                        {ponto}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() =>
                      setFotosPorPonto((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {error && <p className="text-danger">{error}</p>}

        <div className={styles.actions}>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || nota < 1 || nota > 5}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;